import React, { useEffect, useRef, useState } from "react";
import RemoveCardDialog from "./RemoveCardDialog";
import { SLEEVE_FILL_CLASS, shouldPlaySleeveFill, sleeveFillDurationMs } from "../../../wishlist/wishlistMotion";
import { getRarityBadge, getPriceDisplay } from "../../cardDisplay";
import CardImage from "./CardImage";
import { MOTION, MOTION_EASING } from "../../../styles/motionTokens";
import { useCurrency } from "../../../providers/currencyContext";

// Inline heart SVG — filled when wishlisted, outline otherwise
const HeartIcon = ({ filled }) =>
  filled ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#ef4444" stroke="#ef4444" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );

const PLACEHOLDER_IMG = "https://images.pokemontcg.io/base1/1.png";

// Card actions sit in a tight 2-up grid, so they cannot be a full 44px, but
// ~30px hit areas were badly under-sized for thumbs. 40px is the largest that
// still leaves the price room on a 375px viewport.
// 44: the app's touch rule (UserProfile, TradeJourneyPage); 40 measured
// ~41dp on the device (tablet QA, versionCode 11, T-25).
const ACTION_HIT = { minWidth: 44, minHeight: 44 };

const Card = ({
  pokemonCard,
  onClick,
  onToggleWishlist,
  onAddToCollection,
  onListForSale,
  isWishlisted,
  inCollection,
  ownerPhotoUrl,
  bulkMode,
  isSelected,
  onToggleBulkSelect,
  // The grade of the specific copy this tile stands for, when the caller has
  // one (the Collection grid renders UserCards, not catalog rows). Omitted in
  // the catalog and on the wishlist, where there is no physical copy yet, and
  // getPriceDisplay then falls back to DEFAULT_CONDITION exactly as before.
  //
  // This is what lets the Collection header total agree with the tiles under it
  // (P1-47): both sides now apply the same condition multiplier to the same
  // card, instead of the header summing an un-adjusted raw field.
  condition,
  // B-14: this copy is promised to an accepted trade (the owner's collection
  // says so, from the same rule the server refuses a removal with). Marked on
  // the tile, and the remove dialog explains instead of offering Remove.
  promised = false,
}) => {
  const { formatAmount } = useCurrency();
  // Split out of the one `||` chain these two used to share, so the catalogue
  // art stays reachable as a named fallback instead of being discarded the
  // moment an owner photo exists. See the `fallbackSrc` note at the tile image.
  const stockImg = pokemonCard?.images?.small || pokemonCard?.images?.large || PLACEHOLDER_IMG;
  const imgSrc = ownerPhotoUrl || stockImg;
  const priceDisplay = getPriceDisplay(pokemonCard, condition, formatAmount);
  const rarityBadge = getRarityBadge(pokemonCard);
  const isHolo = rarityBadge?.glow;
  const [sleeveFill, setSleeveFill] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const prevWishlisted = useRef(!!isWishlisted);

  useEffect(() => {
    if (
      shouldPlaySleeveFill({
        wasWishlisted: prevWishlisted.current,
        willBeWishlisted: !!isWishlisted,
      })
    ) {
      setSleeveFill(true);
      const t = setTimeout(() => setSleeveFill(false), sleeveFillDurationMs());
      prevWishlisted.current = !!isWishlisted;
      return () => clearTimeout(t);
    }
    prevWishlisted.current = !!isWishlisted;
    return undefined;
  }, [isWishlisted]);

  const handleToggleWishlist = (e) => {
    e.stopPropagation();
    if (onToggleWishlist && pokemonCard?._id)
      onToggleWishlist(pokemonCard._id, pokemonCard.name);
  };

  const handleAddToCollection = (e) => {
    e.stopPropagation();
    if (!onAddToCollection || !pokemonCard?._id) return;
    // P0-17: when the badge is in the "in collection" state, callers wire this
    // handler to a toggle that hard-deletes the UserCard record (photos,
    // condition, asking price and notes go with it). Never fire the destructive
    // path without confirmation. Adds (inCollection=false) still go straight
    // through — no dialog for the safe direction.
    if (inCollection) {
      setPendingRemove(true);
      return;
    }
    onAddToCollection(pokemonCard);
  };

  const confirmRemove = () => {
    setPendingRemove(false);
    if (onAddToCollection && pokemonCard?._id) onAddToCollection(pokemonCard);
  };

  // The remove confirm registers with the Android Back stack itself, while it
  // is on screen (RemoveCardDialog, label "card-remove-confirm"), so Back
  // cancels it instead of leaving the page with the question open. The full
  // history of why is kept there.

  const handleListForSale = (e) => {
    e.stopPropagation();
    if (onListForSale && pokemonCard?._id) onListForSale(pokemonCard);
  };

  const handleBulkToggle = (e) => {
    e.stopPropagation();
    if (onToggleBulkSelect && pokemonCard?._id && !inCollection) {
      onToggleBulkSelect(pokemonCard);
    }
  };

  const bulkChecked = bulkMode && (inCollection || isSelected);

  return (
    <>
    <div
      className={`card-float${isHolo ? " holo-effect" : ""}`}
      data-testid={`market-card-${pokemonCard?._id || "unknown"}`}
      onClick={bulkMode ? handleBulkToggle : onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: isHolo
          ? "0 8px 32px rgba(107,33,168,0.18), 0 2px 8px rgba(0,0,0,0.08)"
          : "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        border: bulkChecked
          ? "2px solid #7c3aed"
          : isHolo
            ? "1.5px solid rgba(107,33,168,0.22)"
            : "1.5px solid rgba(107,33,168,0.1)",
        cursor: "pointer",
        width: "100%",
        opacity: bulkMode && inCollection ? 0.6 : 1,
        transition: `transform ${MOTION.pressMs}ms ${MOTION_EASING.standard}, border 0.15s ease, opacity 0.15s ease`,
        WebkitTapHighlightColor: "transparent",
      }}
      onPointerDown={(e) => {
        e.currentTarget.style.transform = `scale(${MOTION.pressScale})`;
      }}
      onPointerUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      onPointerLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
      onPointerCancel={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "3/4",
          background: "#f1f5f9",
          overflow: "hidden",
        }}
      >
        <CardImage
          src={imgSrc}
          alt={pokemonCard?.name || "Pokemon Card"}
          // An owner photo is hosted somewhere else entirely, so it can 404
          // on its own while the catalogue art is fine. When it does, the
          // honest substitute is THIS card's official art — which we have
          // in `stockImg` — not CardImage's global last-resort image. With
          // no owner photo `src` already IS `stockImg`, so leave that path
          // on CardImage's own default rather than pointing it at itself.
          fallbackSrc={ownerPhotoUrl ? stockImg : undefined}
          loading="lazy"
          style={{
            objectFit: "cover",
            transition: "transform 0.6s ease, opacity 320ms ease",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.08)")}
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        />

        {bulkMode && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 11,
              width: 28,
              height: 28,
              borderRadius: 8,
              background: bulkChecked ? "#7c3aed" : "rgba(255,255,255,0.9)",
              border: bulkChecked ? "2px solid #7c3aed" : "2px solid #cbd5e1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(6px)",
              transition: "all 0.15s ease",
              pointerEvents: "none",
            }}
          >
            {bulkChecked && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        )}

        {promised && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
            }}
          >
            {[{ color: "#334155", label: "In a trade" }]
              .filter(Boolean)
              .map((badge) => (
                <span
                  key={badge.label}
                  data-testid={badge.label === "In a trade" ? "card-promised-badge" : undefined}
                  style={{
                    background: badge.color + "ee",
                    backdropFilter: "blur(8px)",
                    padding: "2px 8px",
                    borderRadius: 8,
                    fontSize: 9,
                    fontWeight: 900,
                    color: "#fff",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  {badge.label}
                </span>
              ))}
          </div>
        )}
      </div>

      <div style={{ padding: "10px 12px 12px" }}>
        {/* Rarity sits above the name, not over the art: every card prints its
            name across the top, and an overlay there hid it. */}
        {rarityBadge && (
          <span
            data-testid="card-rarity"
            style={{
              display: "inline-block",
              maxWidth: "100%",
              boxSizing: "border-box",
              // "Special Illustration Rare" is wider than a 360px phone's tile;
              // wrap it rather than cut the rarity off with an ellipsis.
              whiteSpace: "normal",
              lineHeight: 1.35,
              verticalAlign: "top",
              marginBottom: 6,
              background: rarityBadge.color + "ee",
              padding: "2px 7px",
              borderRadius: 8,
              fontSize: 9,
              fontWeight: 900,
              color: "#fff",
              textTransform: "uppercase",
              letterSpacing: "0.03em",
            }}
          >
            {rarityBadge.label}
          </span>
        )}
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 800,
            color: "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {pokemonCard?.name || "Unknown"}
        </p>
        <div
          data-testid="card-action-row"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            gap: 6,
            // Own the controls on narrow phones (320–430px): if the price + the
            // action cluster can't share one line, the actions wrap to their own
            // line instead of clipping out of the card (Cluster 5). rowGap keeps
            // the wrapped line from touching the price.
            flexWrap: "wrap",
            rowGap: 8,
          }}
        >
          {/* Never a bare dollar amount: these are multiplier estimates off a
              market average, not observed sales. */}
          {priceDisplay.hasPrice ? (
            <span
              data-testid="card-price"
              style={{
                display: "flex", alignItems: "baseline", gap: 3,
                fontFamily: "Inter, sans-serif", flexShrink: 0,
              }}
            >
              {priceDisplay.qualifier && (
                <span style={{
                  fontSize: 10, fontWeight: 800, color: "#64748b",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                }}>
                  {priceDisplay.qualifier}
                </span>
              )}
              <span style={{ fontSize: 17, fontWeight: 900, color: "#6b21a8" }}>
                {priceDisplay.amount}
              </span>
            </span>
          ) : (
            <span
              data-testid="card-price"
              style={{
                fontSize: 11, fontWeight: 700, color: "#64748b",
                fontFamily: "Inter, sans-serif", flexShrink: 0,
              }}
            >
              {priceDisplay.label}
            </span>
          )}

          {bulkMode ? (
            <span style={{
              fontSize: 11, fontWeight: 800, flexShrink: 0,
              color: inCollection ? "#10b981" : isSelected ? "#7c3aed" : "#64748b",
            }}>
              {inCollection ? "Owned" : isSelected ? "Selected" : "Tap to select"}
            </span>
          ) : (
            <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: "auto" }}>
              {onToggleWishlist && (
                <button
                  type="button"
                  data-testid="wishlist-heart"
                  data-sleeve-fill={sleeveFill ? "true" : "false"}
                  className={sleeveFill ? SLEEVE_FILL_CLASS : undefined}
                  onClick={handleToggleWishlist}
                  style={{
                    background: isWishlisted ? "rgba(239,68,68,0.1)" : "rgba(107,33,168,0.08)",
                    color: isWishlisted ? "#ef4444" : "#94a3b8",
                    border: "none", borderRadius: 10, padding: "7px 8px", ...ACTION_HIT,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", transition: "all 0.2s ease",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <HeartIcon filled={isWishlisted} />
                </button>
              )}

              {onListForSale && inCollection && (
                <button
                  type="button"
                  onClick={handleListForSale}
                  style={{
                    background: "rgba(245,158,11,0.15)",
                    color: "#d97706",
                    border: "none", borderRadius: 10, padding: "7px 8px", ...ACTION_HIT,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", transition: "all 0.2s ease",
                    fontSize: 11, fontWeight: 800,
                  }}
                  aria-label="List for sale or trade"
                >
                  List
                </button>
              )}

              {onAddToCollection && (
                <button
                  type="button"
                  data-testid="add-to-collection"
                  onClick={handleAddToCollection}
                  style={{
                    background: inCollection ? "rgba(16,185,129,0.15)" : "#6b21a8",
                    color: inCollection ? "#10b981" : "#fff",
                    border: "none", borderRadius: 10, padding: "7px 10px", ...ACTION_HIT,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", transition: "all 0.2s ease",
                    fontSize: 15, fontWeight: 900, lineHeight: 1,
                    boxShadow: inCollection ? "none" : "0 4px 12px rgba(107,33,168,0.35)",
                  }}
                  aria-label={inCollection ? "Remove from collection" : "Add to collection"}
                >
                  {inCollection ? "✓" : "+"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
      {/* [B-13] A SIBLING of the tile, not its child. The dialog is portalled
          to <body>, but React bubbles its events up the COMPONENT tree: as a
          child of the tile, Cancel and Remove also hit the tile's onClick and
          opened the card's detail sheet, which then hid the "promised to a
          trade" refusal. Not fixed with an onClick on the Dialog: that prop
          replaces MUI's own backdrop handler, so a tap outside stops closing. */}
      {pendingRemove && (
        <RemoveCardDialog
          idPrefix="card-remove"
          promised={promised}
          onCancel={() => setPendingRemove(false)}
          onConfirm={confirmRemove}
        />
      )}
    </>
  );
};

export default Card;
